import { useCallback } from "react";

import { FormattedMessage, injectIntl } from "react-intl";
import type { intlFormatPropTypes } from "react-intl";
import { useSelector } from "react-redux";

import { Text } from "@ps-design-system/components/Text";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { Links } from "../../constants/links.consts";
import { trackRecommendedItemClicked } from "../analytics/host-hub-analytics";
import useRecommendedItems from "../hooks/useRecommendedItems";
import useSubmitGuestReviews from "../hooks/useSubmitGuestReviews";
import useUnreadSpaceReviews from "../hooks/useUnreadSpaceReviews";

import HostHubRecommendedItem from "./HostHubRecommendedItem.tsx";
import HostHubSubmitGuestReviewItem from "./HostHubSubmitGuestReviewItem.tsx";
import HostHubUnreadReviewsGroup from "./HostHubUnreadReviewsGroup.tsx";

type HostHubRecommendedSectionProps = {
  intl: intlFormatPropTypes;
};

const HostHubRecommendedSection = ({
  intl,
}: HostHubRecommendedSectionProps) => {
  const ssoid = useSelector(selectProfileSSOid);

  const recommendedCards = useRecommendedItems(intl);
  const { cards: unreadReviewCards, markSpaceReviewsViewed } =
    useUnreadSpaceReviews();
  const { cards: submitGuestReviewCards, refetch: refetchSubmitGuestReviews } =
    useSubmitGuestReviews();

  const handleUnreadSpaceClick = useCallback(
    (spaceId: string) => {
      trackRecommendedItemClicked({ parameter: "unread_reviews", ssoid });
      // Let the "mark as seen" PUT settle before the full-page navigation tears
      // down this context, so the request isn't aborted in flight.
      void markSpaceReviewsViewed(spaceId)
        .catch(() => {})
        .finally(() =>
          window.location.assign(
            `${Links.ReviewsAboutSpaces}?space=${spaceId}`,
          ),
        );
    },
    [markSpaceReviewsViewed, ssoid],
  );

  // Hide the whole section when none of its sources have anything to show.
  const hasContent = [
    recommendedCards,
    unreadReviewCards,
    submitGuestReviewCards,
  ].some((items) => items.length > 0);
  if (!hasContent) return null;

  return (
    <div className="tw-mt-[16px] tw-flex tw-flex-col tw-gap-[16px]">
      <Text className="tw-my-0" typeface="body-1-m">
        <FormattedMessage
          defaultMessage="Recommended"
          id="host_hub.todo_list.recommended"
        />
      </Text>
      <div className="tw-flex tw-flex-col tw-gap-[16px]">
        {submitGuestReviewCards.map((card) => (
          <HostHubSubmitGuestReviewItem
            card={card}
            key={card.reviewId}
            onReviewSubmitted={refetchSubmitGuestReviews}
          />
        ))}
        {recommendedCards.map((card) => (
          <HostHubRecommendedItem card={card} key={card.key} />
        ))}
        <HostHubUnreadReviewsGroup
          cards={unreadReviewCards}
          onSpaceClick={handleUnreadSpaceClick}
        />
      </div>
    </div>
  );
};

export default injectIntl(HostHubRecommendedSection);
